import { ParserError } from './errors.js';
/** Defaults per the Sol R1 disposition; no committed fixture comes near them. */
export const ZIP_READ_LIMITS = {
    maxEntries: 8192,
    maxEntryBytes: 128 * 1024 * 1024,
    maxTotalBytes: 256 * 1024 * 1024,
    maxCompressionRatio: 200,
    ratioCheckMinBytes: 64 * 1024,
};
function abortError() {
    return Object.assign(new Error('cancelled'), { name: 'AbortError' });
}
/**
 * N-01: raw decompression/CRC errors out of the inflate stream (pako's
 * "invalid block type", "incorrect data check", …) used to escape UNTYPED.
 * The parser error contract is total: map them to corrupt-file, passing
 * AbortError and already-typed ParserErrors through byte-identically.
 */
function toTypedZipError(error) {
    if (error instanceof ParserError)
        return error;
    if (error instanceof Error && error.name === 'AbortError')
        return error;
    const detail = error instanceof Error && error.message.length > 0
        ? `Archive entry could not be decompressed: ${error.message}`
        : undefined;
    return new ParserError('corrupt-file', detail);
}
/** Zip-slip-free guard on the archive's declared entry count. */
export function assertZipEntryCount(count, limits = ZIP_READ_LIMITS) {
    if (count > limits.maxEntries) {
        throw new ParserError('corrupt-file', 'Archive declares too many entries.');
    }
}
/** OPF structures are attacker-controlled too: bound manifest/spine sizes. */
export function assertStructureCount(count, what, limits = ZIP_READ_LIMITS) {
    if (count > limits.maxEntries) {
        throw new ParserError('corrupt-file', `Archive ${what} is too large.`);
    }
}
/**
 * One budget per parse: every entry read draws down the shared total, so a
 * spine of many medium entries cannot dodge the per-entry bound.
 */
export class ZipExpansionBudget {
    limits;
    total = 0;
    constructor(limits = ZIP_READ_LIMITS) {
        this.limits = limits;
    }
    noteExpanded(bytes) {
        this.total += bytes;
        if (this.total > this.limits.maxTotalBytes) {
            throw new ParserError('corrupt-file', 'Archive expands beyond the supported total size.');
        }
    }
}
function assertEntryBounds(expandedBytes, compressedSize, limits) {
    if (expandedBytes > limits.maxEntryBytes) {
        throw new ParserError('corrupt-file', 'Archive entry expands beyond the supported size.');
    }
    if (compressedSize !== undefined &&
        expandedBytes > limits.ratioCheckMinBytes &&
        expandedBytes > compressedSize * limits.maxCompressionRatio) {
        throw new ParserError('corrupt-file', 'Archive entry has a pathological compression ratio.');
    }
}
/**
 * Inflate a zip entry to bytes, incrementally: per-chunk the shared budget,
 * the per-entry byte bound and the compression-ratio bound are re-checked and
 * the abort signal is honored, so a bomb is stopped mid-inflate — the bound
 * aborts DURING decompression, never after the allocation it exists to prevent.
 */
export function readZipEntryBytes(entry, budget, options) {
    const guarded = entry;
    const compressedSize = guarded._data?.compressedSize;
    return new Promise((resolve, reject) => {
        const chunks = [];
        let expandedBytes = 0;
        let settled = false;
        const stream = guarded.internalStream('uint8array');
        const fail = (error) => {
            if (settled)
                return;
            settled = true;
            stream.pause();
            reject(error instanceof Error ? error : new Error(String(error)));
        };
        stream.on('data', (chunk) => {
            if (settled)
                return;
            if (options?.signal?.aborted === true) {
                fail(abortError());
                return;
            }
            expandedBytes += chunk.length;
            try {
                budget.noteExpanded(chunk.length);
                assertEntryBounds(expandedBytes, compressedSize, budget.limits);
            }
            catch (error) {
                fail(error);
                return;
            }
            chunks.push(chunk);
        });
        stream.on('error', (error) => fail(toTypedZipError(error)));
        stream.on('end', () => {
            if (settled)
                return;
            settled = true;
            if (options?.signal?.aborted === true) {
                reject(abortError());
                return;
            }
            const out = new Uint8Array(expandedBytes);
            let offset = 0;
            for (const chunk of chunks) {
                out.set(chunk, offset);
                offset += chunk.length;
            }
            resolve(out);
        });
        stream.resume();
    });
}
/** readZipEntryBytes + UTF-8 decode (BOM preserved, matching JSZip's decoder). */
export async function readZipEntryText(entry, budget, options) {
    const bytes = await readZipEntryBytes(entry, budget, options);
    return new TextDecoder('utf-8', { ignoreBOM: true }).decode(bytes);
}
//# sourceMappingURL=zip.js.map